import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../Navbar";
import OutletSearch from "../SelectSearch/OutletSearch";
import WastageInputForm from "./Wastage/WastageInputForm";
import WastageInputTable from "./Wastage/WastageInputTable";
import Footer from "../Footer";
import { useStock } from "../../context/StockProvider";
import { formatToISODate } from "../../utils/formateDateUtils";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "34px",
        height: "34px",
        fontSize: "0.875rem",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: "#3b82f6" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "34px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 6px" }),
    menu: (base) => ({ ...base, zIndex: 50, fontSize: "0.875rem" }),
    option: (base, { isFocused }) => ({
        ...base,
        backgroundColor: isFocused ? "#EFF6FF" : "white",
        color: "#111827",
    }),
};

const readonlyClass = "w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";
const editableClass = "w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";

const AddWastage = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const location = useLocation();
    const { startDate } = location?.state || "";

    const [selectedOutlet, setSelectedOutlet] = useState();

    const [costCenterOptions, setCostCenterOptions] = useState([]);
    const [loadingCC, setLoadingCC] = useState(false);
    const [errorCC, setErrorCC] = useState("");

    const [selectedDate, setSelectedDate] = useState(
        new Date().toLocaleDateString('en-CA')
    );
    const [selectedItem, setSelectedItem] = useState(null);
    const [mainQuantity, setMainQuantity] = useState("");
    const [subQuantity, setSubQuantity] = useState("");
    const [wastageItems, setWastageItems] = useState([]);
    const [costCenter, setCostCenter] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [minDate, setMinDate] = useState(startDate || null);

    const { stockItems } = useStock();

    // ── Unit mappings (same as AddWastage) ────────────────────────────────────
    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const fetchDates = async () => {
        if (!token || !selectedOutlet || startDate) return;
        setMinDate(null);

        try {
            const foodResponse = await axios.post(`${baseUrl}/get-inventory-period-food`, {
                token: token,
                outlet: selectedOutlet
            });

            const foodData = foodResponse.data;
            const foodIsoStart = formatToISODate(foodData?.opening_date);
            setMinDate(foodIsoStart);

        } catch (err) {
            toast.error("Failed to fetch dates");
        }
    };

    // ── Fetch cost centers ────────────────────────────────────────────────────
    const fetchCostCenters = async () => {
        try {
            setLoadingCC(true);
            setErrorCC("");

            const res = await axios.post(`${baseUrl}/get_costcenters_by_outlet`, {
                outlet_name: selectedOutlet,
                token
            });

            const formatted = (res.data?.data || []).map((cc) => ({
                label: cc.costcenter_name,
                value: cc.costcenter_name,
                id: cc.id,
            }));

            setCostCenterOptions(formatted);
        } catch (error) {
            console.error("Failed to fetch cost centers", error);
            const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                "Failed to load cost centers";
            toast.error(errorMessage);
            setErrorCC(errorMessage);
        } finally {
            setLoadingCC(false);
        }
    };

    useEffect(() => {
        if (!token || !selectedOutlet) return;
        fetchDates();
        fetchCostCenters();
    }, [token, selectedOutlet, baseUrl]);

    // ── Stock item lookup (same as AddWastage) ────────────────────────────────
    const getStockItemByName = (itemName) => {
        return (stockItems || []).find(stock => stock.ItemName === itemName);
    };

    // ── Unit helpers (same as AddWastage) ─────────────────────────────────────
    const shouldShowSecondary = (stockItem) => {
        if (!stockItem) return false;
        const primaryUnit = (stockItem.primary_unit || '').toLowerCase().trim();
        const secondaryUnit = (stockItem.secondary_unit || '').toLowerCase().trim();
        return primaryUnit !== secondaryUnit;
    };

    const computeFinalQuantity = (primaryQty, secondaryQty, stockItem) => {
        const parsedMain = parseFloat(primaryQty) || 0;
        const parsedSub = parseFloat(secondaryQty) || 0;

        const definition = stockItem?.unit_definitions?.[0];
        const unitSize = parseFloat(definition?.unit) || 1;
        const baseUom = (definition?.uom || "").toLowerCase();

        const primaryUnit = (stockItem?.primary_unit || "").toLowerCase();
        const secondaryUnit = (stockItem?.secondary_unit || "").toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations,
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit;
        let secondaryInBaseUnit;

        if (primaryUnit === baseUom) {
            primaryInBaseUnit = parsedMain;
        } else {
            primaryInBaseUnit = parsedMain * unitSize;
        }

        if (secondaryUnit === baseUom) {
            secondaryInBaseUnit = parsedSub;
        } else if (
            isMatch(baseUom, gramVariations) &&
            isMatch(secondaryUnit, kgVariations)
        ) {
            secondaryInBaseUnit = parsedSub * 1000;
        } else if (
            isMatch(baseUom, milliliterVariations) &&
            isMatch(secondaryUnit, literVariations)
        ) {
            secondaryInBaseUnit = parsedSub * 1000;
        } else if (
            isMatch(baseUom, kgVariations) &&
            isMatch(secondaryUnit, gramVariations)
        ) {
            secondaryInBaseUnit = parsedSub / 1000;
        } else if (
            isMatch(baseUom, literVariations) &&
            isMatch(secondaryUnit, milliliterVariations)
        ) {
            secondaryInBaseUnit = parsedSub / 1000;
        } else {
            secondaryInBaseUnit = parsedSub * unitSize;
        }

        const result = (primaryInBaseUnit + secondaryInBaseUnit) / unitSize;

        return result;
    };

    const reverseComputeQuantities = (quantity, stockItem) => {
        const total = parseFloat(quantity) || 0;

        if (!total) {
            return { primaryQty: "", secondaryQty: "" };
        }

        const definition = stockItem?.unit_definitions?.[0];
        const unitSize = parseFloat(definition?.unit) || 1;
        const baseUom = (definition?.uom || "").toLowerCase();

        const primaryUnit = (stockItem?.primary_unit || "").toLowerCase();
        const secondaryUnit = (stockItem?.secondary_unit || "").toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations,
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        const primaryMultiplier = primaryUnit === baseUom ? 1 : unitSize;
        const totalInBaseUnit = total * unitSize;

        if (!shouldShowSecondary(stockItem)) {
            const primaryQty = totalInBaseUnit / primaryMultiplier;
            return {
                primaryQty: primaryQty ? String(parseFloat(primaryQty.toFixed(4))) : "",
                secondaryQty: "",
            };
        }

        let secondaryMultiplier;

        if (secondaryUnit === baseUom) {
            secondaryMultiplier = 1;
        } else if (
            isMatch(baseUom, gramVariations) &&
            isMatch(secondaryUnit, kgVariations)
        ) {
            secondaryMultiplier = unitSize;
        } else if (
            isMatch(baseUom, milliliterVariations) &&
            isMatch(secondaryUnit, literVariations)
        ) {
            secondaryMultiplier = unitSize;
        } else {
            secondaryMultiplier = unitSize;
        }

        const primaryQty = Math.floor(totalInBaseUnit / primaryMultiplier);
        const remainder = totalInBaseUnit - primaryQty * primaryMultiplier;
        const secondaryQty = remainder / secondaryMultiplier;

        return {
            primaryQty: primaryQty ? String(primaryQty) : "",
            secondaryQty: secondaryQty
                ? String(parseFloat(secondaryQty.toFixed(4)))
                : "",
        };
    };

    // ── Item actions ─────────────────────────────────────
    const handleAddItem = () => {
        if (!selectedItem) {
            toast.error("Please select an item");
            return;
        }

        const parsedMain = parseFloat(mainQuantity);
        const parsedSub = parseFloat(subQuantity);

        if (
            (isNaN(parsedMain) || parsedMain <= 0) &&
            (isNaN(parsedSub) || parsedSub <= 0)
        ) {
            toast.error("Enter valid quantity");
            return;
        }

        const alreadyExists = wastageItems.find(item => item.id === selectedItem.id);

        if (alreadyExists && !editingItemId) {
            toast.warn("Item already added. Please edit instead.");
            return;
        }

        const stockItem = getStockItemByName(selectedItem.id);
        const finalQty = computeFinalQuantity(mainQuantity, subQuantity, stockItem);
        const rate = parseFloat(stockItem?.Rate) || 0;
        const cost = finalQty * rate;

        const newItem = {
            id: selectedItem.id,
            item_name: selectedItem.name,
            mainQty: parsedMain || 0,
            subQty: parsedSub || 0,
            quantity: finalQty,
            rate,
            cost,
            uom: selectedItem.uom,
            secondaryUom: selectedItem.secondaryUom
        };

        if (editingItemId) {
            setWastageItems(prev =>
                prev.map(item => item.id === editingItemId ? newItem : item)
            );
            toast.success("Item updated successfully");
        } else {
            setWastageItems(prev => [...prev, newItem]);
            toast.success("Item added successfully");
        }

        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleEditItem = (item) => {
        const stockItem = getStockItemByName(item.item_name);

        if (!stockItem) {
            toast.error("Item not found");
            return;
        }

        const { primaryQty, secondaryQty } = reverseComputeQuantities(item.quantity, stockItem);

        setSelectedItem({
            id: stockItem.ItemName,
            name: stockItem.ItemName,
            uom: stockItem.primary_unit,
            secondaryUom: stockItem.secondary_unit
        });

        setMainQuantity(primaryQty);
        setSubQuantity(secondaryQty);
        setEditingItemId(item.id);
    };

    const handleCancelEdit = () => {
        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleRemoveItem = (id) => {
        setWastageItems(wastageItems.filter(item => item.id !== id));
        if (editingItemId === id) handleCancelEdit();
        toast.success("Item removed");
    };

    const handleSubmit = () => {
        if (!token || !selectedOutlet) {
            toast.error("Please select an outlet");
            return;
        }
        if (wastageItems.length === 0) {
            toast.error("Please add at least one item");
            return;
        }
        if (!costCenter) {
            toast.error("Please enter cost center");
            return;
        }
        if (!selectedDate) {
            toast.error("Please select a date");
            return;
        }

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these wastage stocks?</p>
                <div className="flex gap-2">
                    <button
                        className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                        onClick={async () => {
                            toast.dismiss();
                            setIsLoading(true);
                            try {
                                const payload = {
                                    token,
                                    outlet_name: selectedOutlet,
                                    costcenter: costCenter.value,
                                    received_date: selectedDate,
                                    items: wastageItems.map(item => {
                                        const stockItem = getStockItemByName(item.item_name);
                                        return {
                                            item_name: item.item_name,
                                            quantity: item.quantity ?? computeFinalQuantity(item.mainQty, item.subQty, stockItem),
                                            uom: stockItem?.primary_unit
                                        };
                                    })
                                };

                                await axios.post(`${baseUrl}/wastage-items`, payload);
                                toast.success("Wastage added successfully!");
                                setSelectedDate(new Date().toLocaleDateString('en-CA'));
                                setWastageItems([]);
                                setCostCenter("");
                                setEditingItemId(null);

                                setTimeout(() => {
                                    navigate(-1);
                                }, 1000)
                            } catch (error) {
                                console.error('Error submitting wastage:', error);
                                toast.error(
                                    error?.response?.data?.error ||
                                    error?.response?.data?.message ||
                                    'Error submitting wastage'
                                );
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-wastage-stocks'
            }
        );
    };

    // ── Dropdown options (same shape as AddWastage) ───────────────────────────
    const itemOptions = (stockItems || []).map(stock => ({
        value: stock.ItemName,
        label: stock.ItemName,
        data: {
            id: stock.ItemName,
            name: stock.ItemName,
            uom: stock.primary_unit,
            secondaryUom: stock.secondary_unit
        }
    }));

    const currentStockItem = selectedItem ? getStockItemByName(selectedItem.id) : null;
    const showSubUnitInput = shouldShowSecondary(currentStockItem);

    const liveFinalQty =
        selectedItem && (mainQuantity || subQuantity)
            ? computeFinalQuantity(mainQuantity, subQuantity, currentStockItem)
            : 0;

    const liveRate = parseFloat(currentStockItem?.Rate) || 0;
    const liveCost = liveFinalQty * liveRate;

    const hasSecondaryUnit = shouldShowSecondary;

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl mb-2 mx-auto">
                    <div className="bg-white rounded-lg shadow-md p-4">

                        <div className='flex justify-center'>
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>

                        <h2 className="text-base font-semibold mb-2">Stock Wastage</h2>

                        <WastageInputForm
                            editableClass={editableClass}
                            readonlyClass={readonlyClass}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            loadingCC={loadingCC}
                            errorCC={errorCC}
                            costCenterOptions={costCenterOptions}
                            costCenter={costCenter}
                            setCostCenter={setCostCenter}
                            selectStyles={selectStyles}
                            selectedOutlet={selectedOutlet}
                            itemOptions={itemOptions}
                            selectedItem={selectedItem}
                            setSelectedItem={setSelectedItem}
                            mainQuantity={mainQuantity}
                            setMainQuantity={setMainQuantity}
                            subQuantity={subQuantity}
                            setSubQuantity={setSubQuantity}
                            showSubUnitInput={showSubUnitInput}
                            editingItemId={editingItemId}
                            handleAddItem={handleAddItem}
                            handleCancelEdit={handleCancelEdit}
                            liveCost={liveCost}
                            liveRate={liveRate}
                            startDate={minDate}
                        />

                        <WastageInputTable
                            wastageItems={wastageItems}
                            getStockItemByName={getStockItemByName}
                            hasSecondaryUnit={hasSecondaryUnit}
                            editingItemId={editingItemId}
                            handleEditItem={handleEditItem}
                            handleRemoveItem={handleRemoveItem}
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                        />

                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AddWastage;